import { neutralBrokerLeadLanding } from "./broker-lead-landing-content";
export {
  neutralBrokerLeadLanding,
  brokerLeadLandingForLocale,
} from "./broker-lead-landing-content";
import type { BrokerLeadOffer } from "./broker-lead-offer";
import { z } from "zod/v4";
import { consultationFramingIssue } from "./lead-gen-consultation-framing";

export const BrokerLeadLandingSchema = z.object({
  version: z.literal(1),
  offer: z.enum(["BROKER_CONSULTATION", "FREE_VALUATION"]),
  language: z.enum(["en", "es"]),
  headline: z.string().trim().min(3).max(120),
  description: z.string().trim().min(10).max(350),
});
export type BrokerLeadLanding = z.infer<typeof BrokerLeadLandingSchema>;

const AdCopySchema = z.object({
  language: z.enum(["en", "es"]).optional(),
  broker_title: z.string().optional(),
  primary_text: z.string().optional(),
  facebook_headline: z.string().optional(),
  facebook_description: z.string().optional(),
  call_to_action: z.string().optional(),
  text_options: z
    .object({
      primary_texts: z.array(z.string()).optional(),
      headlines: z.array(z.string()).optional(),
      descriptions: z.array(z.string()).optional(),
    })
    .optional(),
  image_overlay: z
    .object({ bullets: z.array(z.string()).optional() })
    .optional(),
  landing: z.unknown().optional(),
});
type AdCopy = z.infer<typeof AdCopySchema>;
const OTHER_OFFER =
  /\b(valuation|valuations|appraisal|appraisals|instant\s+(?:estimate|quote)|free\s+estimate|business\s+is\s+worth|valoraci[oó]n|valuaci[oó]n|tasaci[oó]n|estimaci[oó]n\s+(?:gratis|gratuita|instant[aá]nea))\b/i;

/** Check every Meta-rotated line, including legacy copy at publication. */
export function brokerLeadConsultationIssues(value: unknown): string[] {
  return brokerLeadOfferIssues(value, "BROKER_CONSULTATION");
}

export function brokerLeadOfferIssues(
  value: unknown,
  offer: BrokerLeadOffer,
): string[] {
  const parsed = AdCopySchema.safeParse(value);
  if (!parsed.success) return ["Ad copy is missing or malformed"];
  const copy = parsed.data;
  const texts = [
    copy.broker_title,
    copy.primary_text,
    copy.facebook_headline,
    copy.facebook_description,
    ...(copy.text_options?.primary_texts ?? []),
    ...(copy.text_options?.headlines ?? []),
    ...(copy.text_options?.descriptions ?? []),
    ...(copy.image_overlay?.bullets ?? []),
  ];
  const issues = texts.some(
    (text) =>
      typeof text === "string" &&
      (offer === "BROKER_CONSULTATION"
        ? OTHER_OFFER.test(text)
        : /\b(instant|guaranteed|certified appraisal|exact (?:price|value))\b/i.test(
            text,
          )),
  )
    ? [
        "Every ad variant must offer a broker consultation, not a valuation or instant estimate",
      ]
    : [];
  if (offer === "FREE_VALUATION") {
    const namesEstimate = /\b(?:estimate|valuation|worth)\b/i;
    const offerLines = [
      copy.primary_text,
      copy.facebook_headline,
      ...(copy.text_options?.primary_texts ?? []),
      ...(copy.text_options?.headlines ?? []),
    ];
    if (
      offerLines.some(
        (text) => typeof text === "string" && !namesEstimate.test(text),
      )
    )
      issues.push(
        "Every primary text and headline must explicitly offer the free business value estimate; generic sale or consultation copy is not a valuation offer.",
      );
  }
  const framing = consultationFramingIssue(
    texts.filter((text): text is string => typeof text === "string"),
  );
  if (framing) issues.push(framing);
  if (
    copy.call_to_action &&
    !["LEARN_MORE", "CONTACT_US"].includes(copy.call_to_action)
  ) {
    issues.push("A consultation must use LEARN_MORE or CONTACT_US");
  }
  return issues;
}

/** Generate from the approved canonical headline; no new claims or runtime AI. */
export function buildBrokerLeadLanding(
  value: unknown,
  offer: BrokerLeadOffer = "BROKER_CONSULTATION",
): BrokerLeadLanding | null {
  if (
    !value ||
    typeof value !== "object" ||
    brokerLeadOfferIssues(value, offer).length
  )
    return null;
  const copy = AdCopySchema.parse(value);
  const headline = copy.facebook_headline?.trim();
  if (!headline) return null;
  const parsed = BrokerLeadLandingSchema.safeParse({
    ...neutralBrokerLeadLanding(copy.language ?? "en", offer),
    headline,
  });
  return parsed.success ? parsed.data : null;
}

/** Legacy intros are derived without writes; stale or untrusted snapshots cannot add claims. */
export function resolveBrokerLeadLanding(
  value: unknown,
  offer: BrokerLeadOffer = "BROKER_CONSULTATION",
): BrokerLeadLanding | null {
  const expected = buildBrokerLeadLanding(value, offer);
  if (!expected) return null;
  const stored = BrokerLeadLandingSchema.safeParse((value as AdCopy).landing);
  return stored.success &&
    JSON.stringify(stored.data) === JSON.stringify(expected)
    ? stored.data
    : expected;
}
